// B2B PDP order grid + cross-PDP sticky order bar.
//
// Regime B ("B2B engine/09_price_visibility.md"): this entire file is a no-op
// for retail buyers — gated on the same document.documentElement.dataset.b2b
// flag the pre-paint snippet sets. Colour picked -> qty per size -> "Add to
// order" appends to a localStorage basket (variant id + quantity ONLY, never a
// price — the browser cannot set what it will be charged). The sticky bar
// carries that basket across PDPs and submits it to POST /apps/portal/order,
// which prices every line server-side from b2b.price_list, exactly like the
// portal grid. This never touches /cart/add.js.
//
// Reviewing and placing the order is the portal's job, not this file's: the
// bar's "Review order" navigates to /apps/portal/?review=1 and the portal picks
// the basket up from there. That is also why no ladder maths lives here any
// more — the only price this file renders is the starting-tier one /prices
// returns, and every laddered total is worked out on the review screen.
(function () {
  'use strict';
  if (document.documentElement.dataset.b2b !== '1') return;

  var BASKET_KEY = 'mgp_b2b_basket';
  var COLOUR_RE = /colou?r/i;

  // Session lives in ?stok=, not a cookie (App Proxy drops Set-Cookie — see
  // b2b.js / portal.js's own withTok). This file runs on the PDP, a page the
  // login redirect never visits, so its only source for the token is the copy
  // portal.js saved to localStorage on sign-in.
  function withTok(url) {
    var t;
    try { t = localStorage.getItem('mgp_b2b_stok'); } catch (e) { t = null; }
    if (!t) return url;
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'stok=' + encodeURIComponent(t);
  }

  // GET /prices caps a single request at 100 variant ids (VARIANT_LIMIT in
  // b2b.js) — a large colour x size product (e.g. a 250-variant tee) can
  // exceed that on its own, so the grid must ask in batches and merge the
  // results rather than sending every variant on the page in one call.
  var PRICE_CHUNK = 100;
  function fetchPrices(prefix, ids, qty) {
    var chunks = [];
    for (var i = 0; i < ids.length; i += PRICE_CHUNK) chunks.push(ids.slice(i, i + PRICE_CHUNK));
    return Promise.all(
      chunks.map(function (chunk) {
        var url = prefix + '/prices?variants=' + encodeURIComponent(chunk.join(',')) + (qty ? '&qty=' + qty : '');
        return fetch(withTok(url)).then(function (r) {
          return r.json().then(function (d) { return { ok: r.ok, d: d }; });
        });
      })
    ).then(function (results) {
      var prices = [];
      for (var j = 0; j < results.length; j++) {
        var res = results[j];
        if (!res.ok || !res.d.ok) throw new Error((res.d && res.d.error) || 'prices unavailable');
        prices = prices.concat(res.d.prices);
      }
      return { ok: true, prices: prices };
    });
  }

  function money(c) {
    return '$' + (c / 100).toFixed(2);
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function loadBasket() {
    try {
      var raw = JSON.parse(localStorage.getItem(BASKET_KEY) || '[]');
      return Array.isArray(raw) ? raw : [];
    } catch (e) {
      return [];
    }
  }
  function saveBasket(lines) {
    localStorage.setItem(BASKET_KEY, JSON.stringify(lines));
  }
  function addToBasket(newLines) {
    var basket = loadBasket();
    newLines.forEach(function (line) {
      var existing = basket.filter(function (l) {
        return l.variantId === line.variantId;
      })[0];
      if (existing) existing.quantity += line.quantity;
      else basket.push({ variantId: line.variantId, quantity: line.quantity });
    });
    saveBasket(basket);
    renderBar();
  }

  // --- PDP grid ----------------------------------------------------------------

  function initGrid() {
    var root = document.querySelector('[data-b2b-grid]');
    if (!root) return;
    var prefix = root.dataset.prefix || '/apps/portal';
    var msgEl = root.querySelector('[data-b2b-msg]');
    var coloursEl = root.querySelector('[data-b2b-colours]');
    var sizesEl = root.querySelector('[data-b2b-sizes]');
    var addBtn = root.querySelector('[data-b2b-add]');

    var namesScript = document.querySelector('script[data-b2b-option-names]');
    var variantsScript = document.querySelector('script[data-b2b-variants]');
    var optionNames, rawVariants;
    try {
      optionNames = JSON.parse(namesScript.textContent);
      rawVariants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      // Fail closed like the price fetch below: unknown state, so keep the
      // grid (and its error message) reachable rather than silently falling
      // through to a native buy button that might undercut a real contract.
      document.documentElement.dataset.b2bProductContracted = '1';
      msgEl.setAttribute('role', 'alert');
      msgEl.textContent = 'This product could not be loaded for ordering.';
      return;
    }

    var rowName = optionNames.filter(function (n) { return COLOUR_RE.test(n); })[0] || optionNames[0];
    var rowIndex = optionNames.indexOf(rowName);

    var ids = rawVariants.map(function (v) { return v.id; });
    fetchPrices(prefix, ids, 1)
      .then(function (res) {
        var priced = new Map();
        res.prices.forEach(function (p) {
          // inRange (not source) is the membership test: a range-only entry
          // ("in range, at retail") resolves at retail same as a variant with
          // no entry at all, so source alone can't tell them apart — see the
          // comment on GET /prices in b2b.js. `p.source !== 'retail'` is kept
          // as a fallback for a theme deploy that lands ahead of the Worker's
          // `inRange` field — without it, that ordering would blank every
          // grid until the Worker catches up.
          if (p.inRange || p.source !== 'retail') priced.set(p.variantId, p);
        });
        var variants = rawVariants
          .filter(function (v) { return priced.has(v.id); })
          .map(function (v) {
            var price = priced.get(v.id);
            return {
              id: v.id,
              available: v.available,
              row: v.options[rowIndex] || v.options[0],
              size: v.options.filter(function (_, i) { return i !== rowIndex; }).join(' / ') || v.title,
              priceCents: Math.round(parseFloat(price.price) * 100),
              retailCents: price.retail != null ? Math.round(parseFloat(price.retail) * 100) : null,
              ladder: (price.ladder || []).map(function (r) {
                return { min_qty: r.min_qty, priceCents: Math.round(parseFloat(r.price) * 100) };
              }),
            };
          });
        if (!variants.length) {
          // Nothing on this product is in this buyer's range at all — keep
          // the box visible (native retail button still works alongside it,
          // same as any partially-ranged product) but replace the loading
          // message with a self-serve "add it yourself" prompt instead of
          // hiding it, which is the one case this buyer can act on.
          document.documentElement.dataset.b2bProductContracted = '1';
          showAddPrompt(root, msgEl, prefix, root.dataset.productId);
          return;
        }
        document.documentElement.dataset.b2bProductContracted = '1';
        msgEl.hidden = true;
        renderGrid(variants);
      })
      .catch(function () {
        // Fail closed: unknown state, so keep the grid (and its error
        // message) reachable rather than risk a contracted buyer's native
        // button showing instead. The invariant, in code either way: a
        // failed price fetch never shows a retail-priced grid.
        document.documentElement.dataset.b2bProductContracted = '1';
        msgEl.setAttribute('role', 'alert');
        msgEl.textContent = 'Your prices could not be loaded, so the order grid is unavailable. Reload the page.';
      });

    var byRow = new Map();
    var qty = new Map();

    function renderGrid(variants) {
      variants.forEach(function (v) {
        if (!byRow.has(v.row)) byRow.set(v.row, []);
        byRow.get(v.row).push(v);
      });
      var rows = [...byRow.keys()];
      coloursEl.hidden = false;
      coloursEl.setAttribute('role', 'group');
      coloursEl.setAttribute('aria-label', 'Colour');
      coloursEl.innerHTML = rows
        .map(function (row, i) {
          return '<button type="button" aria-pressed="' + (i === 0 ? 'true' : 'false') +
            '" class="b2b-grid__colour' + (i === 0 ? ' is-selected' : '') + '" data-row="' +
            esc(row) + '">' + esc(row) + '</button>';
        })
        .join('');
      coloursEl.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-row]');
        if (!btn) return;
        coloursEl.querySelectorAll('.b2b-grid__colour').forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-selected', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        selectRow(btn.dataset.row);
      });
      selectRow(rows[0]);

      addBtn.disabled = false;
      addBtn.addEventListener('click', function () {
        var lines = [];
        sizesEl.querySelectorAll('input[data-v]').forEach(function (input) {
          var q = parseInt(input.value, 10);
          if (Number.isInteger(q) && q > 0) lines.push({ variantId: input.dataset.v, quantity: q });
        });
        if (!lines.length) return;
        addToBasket(lines);
        sizesEl.querySelectorAll('input[data-v]').forEach(function (i) { i.value = ''; });
        msgEl.hidden = false;
        msgEl.textContent = 'Added to your order. Pick another colour, or check the bar below to submit.';
        setTimeout(function () { msgEl.hidden = true; }, 4000);
      });
    }

    function selectRow(row) {
      var list = byRow.get(row) || [];
      sizesEl.hidden = false;
      sizesEl.setAttribute('aria-live', 'polite');
      sizesEl.innerHTML = list
        .map(function (v) {
          var rrp = v.retailCents != null && v.retailCents > v.priceCents
            ? ' <span class="b2b-grid__rrp">RRP ' + money(v.retailCents) + '</span>' : '';
          return '<label class="b2b-grid__size' + (v.available ? '' : ' is-oos') + '">' +
            '<span>' + esc(v.size) + '</span>' +
            '<span class="b2b-grid__price">' + money(v.priceCents) + '</span>' + rrp +
            '<input type="number" inputmode="numeric" min="0" step="1" value="" ' +
            (v.available ? '' : 'disabled ') + 'data-v="' + esc(v.id) + '" aria-label="Quantity, ' + esc(v.size) + '">' +
            '</label>';
        })
        .join('');
    }
  }

  // Self-serve: this buyer has nothing on this product in their range yet.
  // Adds every variant as a range-only ("in range, at retail") entry — never
  // a price the buyer sets themselves, see POST /add-to-range in b2b.js.
  // Reloads on success rather than re-fetching and re-rendering in place:
  // the price fill, this grid, and the native buy-button gate all key off a
  // fresh page load anyway, so a second render path here would just be a
  // second place for the three to drift out of sync.
  function showAddPrompt(root, msgEl, prefix, productId) {
    var addBtn = root.querySelector('[data-b2b-add]');
    if (addBtn) addBtn.hidden = true;
    msgEl.hidden = false;
    msgEl.removeAttribute('role');
    msgEl.textContent = "This product isn't in your trade range yet.";
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'button b2b-grid__add-range';
    btn.textContent = 'Add to my trade range (at retail)';
    (root.querySelector('.b2b-grid-inner') || root).appendChild(btn);
    btn.addEventListener('click', function () {
      btn.disabled = true;
      btn.textContent = 'Adding…';
      fetch(withTok(prefix + '/add-to-range'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product: productId }),
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
        .then(function (res) {
          if (!res.ok || !res.d.ok) throw new Error((res.d && res.d.error) || 'failed');
          msgEl.textContent = 'Added to your trade range — reloading…';
          location.reload();
        })
        .catch(function (err) {
          btn.disabled = false;
          btn.textContent = 'Add to my trade range (at retail)';
          msgEl.setAttribute('role', 'alert');
          msgEl.textContent = err && err.message ? err.message : 'Could not add this product. Try again.';
        });
    });
  }

  // --- sticky bar (persists across PDPs, reads the localStorage basket) --------

  var bar = null;

  function ensureBar() {
    if (bar) return bar;
    bar = document.createElement('div');
    bar.className = 'b2b-bar';
    bar.innerHTML =
      '<div class="b2b-bar__row">' +
      '<span class="b2b-bar__units" data-b2b-bar-units></span>' +
      '<button type="button" class="button b2b-bar__review" data-b2b-bar-review>Review order</button>' +
      '</div>';
    document.body.appendChild(bar);
    bar.querySelector('[data-b2b-bar-review]').addEventListener('click', goToPortalReview);
    return bar;
  }

  function renderBar() {
    var basket = loadBasket();
    var units = basket.reduce(function (sum, l) { return sum + l.quantity; }, 0);
    if (!units) {
      if (bar) bar.hidden = true;
      return;
    }
    var el = ensureBar();
    el.hidden = false;
    el.querySelector('[data-b2b-bar-units]').textContent = units + (units === 1 ? ' unit in your order' : ' units in your order');
  }

  // "Review order" hands the buyer back to the portal's own review screen
  // rather than opening a second one here. There used to be a review panel and
  // an order-placing POST in this file: a stripped-down twin of the portal
  // review screen with no delivery address, no order note, no per-line quantity
  // edit and no customisation lines — two order flows to keep in step, and a
  // buyer who reached one of them had no idea the other existed. The portal
  // drains this basket into its own grid on arrival (seedFromStorefrontBasket
  // in quote-cart-worker/portal.js), so the bar empties as the order moves.
  function goToPortalReview() {
    if (!loadBasket().length) return;
    var prefix = (document.querySelector('[data-b2b-grid]') || {}).dataset || {};
    location.href = withTok((prefix.prefix || '/apps/portal') + '/?review=1');
  }

  initGrid();
  renderBar();
})();
